import { Box, Typography } from "@mui/material";
import { motion, useTransform } from "motion/react";
import { landingColors } from "../../landingColors";
import AppScreenshot from "../AppScreenshot";

// How far a neighbouring card shrinks and fades at one full card's distance
// from centre. Subtle on purpose: enough to make the centre card read as the
// focused one, not so much that the peeking neighbour looks broken.
const NEIGHBOUR_SCALE_DROP = 0.08;
const NEIGHBOUR_OPACITY_DROP = 0.45;
// Screenshot drifts against the swipe, so the phone reads as sitting behind
// the card rather than printed onto it.
const PARALLAX_PX = 20;

// One card in the mobile deck. Scale/fade/parallax are driven continuously
// from the deck's scroll position rather than from a discrete active index,
// so they track the finger through the whole swipe instead of snapping at the
// end of it.
const StepDeckCard = ({ step, index, count, progress, reducedMotion }) => {
  // Signed distance from this card to the deck's centre, in cards.
  const offset = useTransform(
    progress,
    (value) => value * Math.max(count - 1, 1) - index,
  );
  const distance = useTransform(offset, (value) =>
    Math.min(Math.abs(value), 1),
  );

  const scale = useTransform(distance, (d) => 1 - NEIGHBOUR_SCALE_DROP * d);
  const opacity = useTransform(distance, (d) => 1 - NEIGHBOUR_OPACITY_DROP * d);
  const parallaxX = useTransform(offset, (value) => {
    const clamped = Math.max(Math.min(value, 1), -1);
    return clamped * -PARALLAX_PX;
  });

  const motionStyle = reducedMotion ? undefined : { scale, opacity };

  return (
    <Box
      component={motion.div}
      style={motionStyle}
      sx={{
        flex: "0 0 82%",
        scrollSnapAlign: "center",
        scrollSnapStop: "always",
        bgcolor: landingColors.surface,
        boxShadow: landingColors.cardShadow,
        borderRadius: 3,
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography
        aria-hidden
        sx={{
          fontWeight: 800,
          fontSize: 14,
          letterSpacing: 0.6,
          color: landingColors.primary,
          mb: 0.5,
        }}
      >
        {step.step}
      </Typography>
      <Typography
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: 19,
          lineHeight: 1.3,
          color: landingColors.textPrimary,
          m: 0,
        }}
      >
        {step.title}
      </Typography>
      <Typography
        sx={{
          color: landingColors.textSecondary,
          mt: 1.25,
          mb: 2.5,
          fontSize: 15,
          lineHeight: 1.55,
          // Fixed to exactly 4 lines (clamped if longer, padded if shorter)
          // so every card's screenshot starts at the same vertical position
          // regardless of how much description text a step has.
          height: `${1.55 * 4}em`,
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {step.description}
      </Typography>

      <Box sx={{ width: "100%", maxWidth: 200, overflow: "hidden" }}>
        <Box
          component={motion.div}
          style={reducedMotion ? undefined : { x: parallaxX }}
        >
          <AppScreenshot
            src={step.screenshot.src}
            alt={step.screenshot.alt}
            platform={step.screenshot.platform}
            variant="phone"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default StepDeckCard;
